import { currentBrowserStorageOwner } from "./browserStorageScope.js";

// The register reading a viewer opened from a file, kept in this browser so a
// reload restores it. One IndexedDB database per tenant-and-user owner (as the
// label cache keeps one), holding the file's text under a single key. Every
// call is fail-soft: without a verified owner or IndexedDB nothing is kept and
// callers fall back to the reading they hold in memory.
//
// The reading profiles a tenant's people and practices, so sign-out and
// "Clear local data" drop it with the chat bucket (clearUserData.js).
const DB_PREFIX = "register-reading-v1";
const STORE = "reading";
const KEY = "current";

const dbName = (owner) => `${DB_PREFIX}:${owner.key}`;

function run(owner, mode, op) {
  return new Promise((resolve, reject) => {
    if (!owner || typeof indexedDB === "undefined") {
      reject(new Error("browser persistence is unavailable"));
      return;
    }
    const open = indexedDB.open(dbName(owner), 1);
    open.onupgradeneeded = () => {
      // Only a write creates the database: a read that finds none aborts the
      // create, so looking after a forget or a wipe leaves nothing behind.
      if (mode !== "readwrite") {
        open.transaction.abort();
        return;
      }
      if (!open.result.objectStoreNames.contains(STORE)) open.result.createObjectStore(STORE);
    };
    open.onerror = () => reject(open.error);
    open.onsuccess = () => {
      const db = open.result;
      try {
        const tx = db.transaction(STORE, mode);
        const request = op(tx.objectStore(STORE));
        tx.oncomplete = () => { db.close(); resolve(request.result); };
        tx.onerror = () => { db.close(); reject(tx.error); };
        tx.onabort = () => { db.close(); reject(tx.error); };
      } catch (e) {
        db.close();
        reject(e);
      }
    };
  });
}

/** Keep a file's text for `owner`. Resolves true when the browser holds it. */
export async function keepRegisterReading(owner, { name, text }) {
  try {
    await run(owner, "readwrite", (store) => store.put({ name, text, keptAt: Date.now() }, KEY));
    return true;
  } catch {
    return false;
  }
}

/** What the browser keeps for `owner`, as { name, text }, or null. */
export async function readKeptRegisterReading(owner) {
  try {
    const record = await run(owner, "readonly", (store) => store.get(KEY));
    return record && typeof record.text === "string" ? { name: String(record.name || ""), text: record.text } : null;
  } catch {
    return null;
  }
}

/** Delete `owner`'s database, name included. Resolves once the delete settles. */
export function dropKeptRegisterReading(owner) {
  return new Promise((resolve) => {
    if (!owner || typeof indexedDB === "undefined") {
      resolve();
      return;
    }
    try {
      const request = indexedDB.deleteDatabase(dbName(owner));
      request.onsuccess = () => resolve();
      request.onerror = () => resolve();
      request.onblocked = () => resolve();
    } catch {
      resolve();
    }
  });
}

const clearedListeners = new Set();

/** Called with the owner key whenever a wipe drops that owner's reading. */
export function subscribeKeptRegisterReadingCleared(fn) {
  clearedListeners.add(fn);
  return () => clearedListeners.delete(fn);
}

/** Sign-out and "Clear local data": drop the active tenant's kept reading for this user. */
export async function clearKeptRegisterReading(tenantId) {
  const owner = currentBrowserStorageOwner(tenantId);
  if (!owner) return;
  await dropKeptRegisterReading(owner);
  clearedListeners.forEach((fn) => fn(owner.key));
}
